import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Search, ChevronDown, ChevronUp, Copy, ExternalLink, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminMessages — inbox of every submission to /api/contact.
 * Rows are collapsed to a one-liner and expand to show the full body.
 * "Reply" opens the sender's email in the admin's mail client with the
 * subject pre-populated as "Re: {subject}".
 */
export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/contact-messages`, { params: { limit: 200 } });
      setMessages(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load messages"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("en-ZM", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  const query = q.trim().toLowerCase();
  const filtered = query
    ? messages.filter((m) =>
        [m.name, m.email, m.subject, m.body].some((v) => (v || "").toLowerCase().includes(query))
      )
    : messages;

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 lg:px-8" data-testid="admin-messages">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Messages</h1>
          <p className="mt-1 text-slate-600">Every submission from the public Contact form lands here.</p>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, subject…"
            data-testid="messages-search"
            className="w-64 bg-transparent text-sm outline-none placeholder:text-slate-400"
          />
        </div>
      </div>

      {loading ? (
        <div className="mt-8 rounded-3xl bg-white p-10 text-center text-slate-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="mt-8 rounded-3xl bg-white p-12 text-center card-shadow">
          <Inbox className="mx-auto h-10 w-10 text-slate-300" />
          <div className="mt-3 text-sm text-slate-500">
            {query ? "No messages match your search." : "No messages yet."}
          </div>
        </div>
      ) : (
        <div className="mt-6 space-y-2">
          {filtered.map((m) => {
            const isOpen = expanded.has(m.id);
            const mailtoHref = `mailto:${m.email}?subject=${encodeURIComponent("Re: " + m.subject)}&body=${encodeURIComponent(`\n\n---\nOn ${fmt(m.created_at)}, ${m.name} <${m.email}> wrote:\n${m.body}\n`)}`;
            return (
              <div key={m.id} data-testid={`message-${m.id}`} className="rounded-2xl bg-white card-shadow overflow-hidden">
                <button
                  onClick={() => toggle(m.id)}
                  data-testid={`message-toggle-${m.id}`}
                  className="w-full text-left px-5 py-4 flex items-center gap-3 hover:bg-slate-50"
                >
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-copper/10 text-copper text-sm font-bold">
                    {m.name?.slice(0, 1)?.toUpperCase() || "?"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="truncate font-semibold text-slate-900">{m.name}</span>
                      <span className="truncate text-xs text-slate-500">{m.email}</span>
                    </div>
                    <div className="mt-0.5 truncate text-sm text-slate-700">
                      <b>{m.subject}</b>
                      {!isOpen && m.body && <span className="text-slate-500"> — {m.body.slice(0, 100)}{m.body.length > 100 ? "…" : ""}</span>}
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div className="text-[11px] text-slate-500">{fmt(m.created_at)}</div>
                    {isOpen ? <ChevronUp className="ml-auto mt-1 h-4 w-4 text-slate-400" /> : <ChevronDown className="ml-auto mt-1 h-4 w-4 text-slate-400" />}
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-slate-100 bg-slate-50/60 px-5 py-4" data-testid={`message-body-${m.id}`}>
                    <div className="whitespace-pre-wrap text-sm text-slate-800">{m.body}</div>
                    <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                      <a
                        href={mailtoHref}
                        data-testid={`message-reply-${m.id}`}
                        className="btn-primary text-xs !py-2"
                      >
                        <Mail className="mr-1 inline h-3.5 w-3.5" /> Reply via email
                      </a>
                      <button
                        onClick={() => copy(m.email)}
                        data-testid={`message-copy-${m.id}`}
                        className="btn-outline text-xs !py-2"
                      >
                        <Copy className="mr-1 inline h-3.5 w-3.5" /> Copy address
                      </button>
                      {m.ip && (
                        <span className="ml-auto text-[11px] text-slate-400">IP: {m.ip}</span>
                      )}
                      <span className="text-[11px] text-slate-400">ID: <code>{m.id.slice(-8)}</code></span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-8 text-center text-xs text-slate-400">
        Replies are sent from your mail client — the sender email is set as reply-to on the notification you already received, so hitting Reply there works too.{" "}
        <a
          href="https://resend.com/emails"
          target="_blank"
          rel="noreferrer"
          className="text-copper hover:underline inline-flex items-center gap-0.5"
        >
          Resend dashboard <ExternalLink className="h-3 w-3" />
        </a>
      </p>
    </div>
  );
}
